import React from 'react'
import { Button } from '@components/ui/button'
import { cn } from '@/utils'
import {
  PHOTO_ACTION_DEFINITIONS,
  PHOTO_PLACEHOLDER_ACTION_IDS,
  type PhotoActionDefinition,
  type PhotoActionId,
} from '@/lib/photos/photo-action-items'
import { PHOTO_ACTION_ICONS, PHOTO_ACTION_LOADING_ICON } from './constants/photo-action-icons'

export type PhotoActionHandlers = Partial<Record<PhotoActionId, () => void>>

interface PhotoActionsListProps {
  handlers: PhotoActionHandlers
  isDisabled: (id: PhotoActionId) => boolean
  /** When true, Remove background shows a spinner instead of the eraser icon. */
  isRemovingBackground?: boolean
  /** Actions whose trigger should show the pressed/active chrome (e.g. Photo effects open). */
  isActionActive?: (id: PhotoActionId) => boolean
  /** Which actions to list, in order. Defaults to every photo action; a placed ClikArt passes its
   *  own four (`CLIKART_PHOTO_ACTION_DEFINITIONS`). */
  actions?: PhotoActionDefinition[]
  className?: string
}

export const PhotoActionsList: React.FC<PhotoActionsListProps> = ({
  handlers,
  isDisabled,
  isRemovingBackground = false,
  isActionActive,
  actions = PHOTO_ACTION_DEFINITIONS,
  className,
}) => {
  return (
    <div className={cn('flex flex-col gap-1 w-full', className)}>
      {actions.map(({ id, label, icon }) => {
        const Icon = PHOTO_ACTION_ICONS[icon]
        const onClick = handlers[id]
        const disabled = isDisabled(id) || (!onClick && !PHOTO_PLACEHOLDER_ACTION_IDS.has(id))
        const showLoading = id === 'removeBackground' && isRemovingBackground
        const active = isActionActive?.(id) ?? false
        const expandsEffects = id === 'photoEffects'

        return (
          <Button
            key={id}
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={onClick ?? undefined}
            className={cn(
              'h-8 w-full justify-start gap-2 px-3 text-xs font-medium text-foreground',
              active && 'bg-accent text-accent-foreground'
            )}
            aria-busy={showLoading || undefined}
            aria-label={label}
            aria-pressed={active || undefined}
            aria-expanded={expandsEffects ? active : undefined}
          >
            {showLoading ? (
              <PHOTO_ACTION_LOADING_ICON
                className="size-4 shrink-0 animate-spin"
                aria-hidden="true"
              />
            ) : (
              <Icon className="size-4 shrink-0" aria-hidden="true" />
            )}
            {label}
          </Button>
        )
      })}
    </div>
  )
}
